import React, { useState, useEffect, useContext }   from 'react';
import { NotificationManager }                      from 'react-notifications';
import config from '../../../../config';
import { VisibilityIcon, EditIcon, DoDisturbIcon }  from '../../../../assets/icons/icons';
import configMessage from '../../../../utils/configMessage.json';
import './PrivilegeList.css';
import axios  from 'axios';
import { AuthContext } from '../../../../contexts/authContext';

// Definizione delle icone per i diversi stati dei privilegi
const oIconePrivilegi = {
     W: { oComponente: EditIcon       , sClasse: 'write alert-success no-user-select'           , sTitle: 'Write'  }
    ,R: { oComponente: VisibilityIcon , sClasse: 'read alert-warning no-user-select'            , sTitle: 'Read'   }
    ,N: { oComponente: DoDisturbIcon  , sClasse: 'denied alert-secondary no-user-select'        , sTitle: 'Denied' }
};

// Definizione delle icone per i permessi negati
const oIconeNegazione = {
     Y: { oComponente: DoDisturbIcon  , sClasse: 'denied alert-secondary no-user-select'        , sTitle: 'Denied'  }
    ,N: { oComponente: DoDisturbIcon  , sClasse: 'no-denied success-icon-denied no-user-select' , sTitle: 'Allowed' }
};

/**
 * Componente per la gestione della lista dei privilegi
 * @param {string}   kUser        - ID univoco dell'utente di cui gestire i privilegi (es: "USER123")
 * @param {boolean}  isToSave     - Flag che indica se salvare le modifiche ai privilegi.
 *                                  Quando true, attiva il salvataggio se sono state fatte modifiche (es: false -> true)
 * @param {Function} setSaveFalse - Callback per reimpostare isToSave=false dopo il salvataggio
 * @param {boolean}  forRoles     - Flag che indica se verrà usato per i ruoli
 * @param {object}   oPrivilegiRuolo
 * @returns {JSX.Element}         - Interfaccia grafica per gestire i privilegi utente
 *
 * Esempio di utilizzo:
 * <PrivilegeList kUser="USER123" isToSave={bSalva} setSaveFalse={() => setBSalva(false)} />
 */
const PrivilegeList = ( { kUser, save: isToSave, setSaveFalse, forRoles = false, oPrivilegiRuolo } ) => {
    
    const [ bCaricamento , setBCaricamento ] = useState( true );
    const [ bModificato  , setBModificato  ] = useState( false );
    const [ aoPrivilegi  , setAoPrivilegi  ] = useState( forRoles ? [ {}, oPrivilegiRuolo ] : [] );
    const [ aoStruttura  , setAoStruttura  ] = useState( [] );
    
    const auth = useContext(AuthContext);    

    useEffect( () => {
        fCaricaDati();
    }, [] );
    
    useEffect( () => {
        if ( isToSave && bModificato ) {
            fSalvaModifiche();
            setBModificato( false );
        }else{
            fCaricaDati();
        }
    }, [ isToSave ] );
    
    // Carica i dati dei privilegi e la struttura dal backend
    const fCaricaDati = async () => {
        try {
            /* Esempi di risposta dal backend:
            
                oRispostaStruttura.data = [
                    { sSection: "Upload / Import"    , sPrivField: "P_LOAD_NIELSEN_INV_GRP" , sPrivName: "Nielsen: Standard Investments / GRP" },
                    { sSection: "Upload / Import"    , sPrivField: "P_LOAD_NIELSEN_INV_SHR" , sPrivName: "Nielsen: Standard Investments / SHR" },
                    { sSection: "General Privileges" , sPrivField: "P_ALERTS"               , sPrivName: "Alerts"                              }
                ]
                
                oRispostaPrivilegi.data = [
                
                    // indice 0: privilegi diretti
                    { P_ALERTS: 'W', P_CLIENTI: 'R', ... },
                    
                    // indice 1: privilegi ereditati dai ruoli
                    { P_ALERTS: 'N', P_CLIENTI: 'R', ... },
                    
                    // indice 2: privilegi negati
                    {  P_ALERTS: 'Y', P_CLIENTI: 'N', ... },
                    
                    // indice 3: risultato finale
                    { P_ALERTS: 'N', P_CLIENTI: 'R', ... }
                    
               ]
            */
            
            const aRequests = [ fInviaRichiesta( '/users/user-get-privileges-structure', {}        , 'GET' ) ];
            
            if ( !forRoles ) {
                aRequests.push( fInviaRichiesta( '/users/user-get-privileges', { kUser } , 'GET' ) );
            }
            
            // Caricamento parallelo dei dati
            const [ aoRispostaStruttura, aoRispostaPrivilegi ] = await Promise.all( aRequests);
            
            /*
                struttura finale risultante:
                [
                    { 
                        sSection: "Upload / Import",
                        aoPrivilegiDiSezione: [
                            { 
                                sPrivilegio:    "P_LOAD_NIELSEN_INV_GRP",
                                sDescrizione:   "Nielsen: Standard Investments / GRP"
                            },
                            {
                                sPrivilegio:    "P_LOAD_NIELSEN_INV_SHR",
                                sDescrizione:   "Nielsen: Standard Investments / SHR"
                            }
                        ]
                    },
                    {
                        sSection: "General Privileges",
                        aoPrivilegiDiSezione: [
                            {
                                sPrivilegio:    "P_ALERTS",
                                sDescrizione:   "Alerts"
                            },
                            {
                                sPrivilegio:    "P_TARGET",
                                sDescrizione:   "Targets management"
                            }
                        ]
                    }
                ]
            
            */
            const aoStruttura = aoRispostaStruttura.data.reduce( ( ao, o ) => {
                
                const oSection = ao.find( s => s.sSection === o.sSection );
                
                if ( oSection ) {
                    oSection.aoPrivilegiDiSezione.push({
                         sPrivilegio:  o.sPrivField
                        ,sDescrizione: o.sPrivName
                    });
                    
                } else {
                    ao.push({
                         sSection:               o.sSection
                        ,aoPrivilegiDiSezione:   [{
                             sPrivilegio:  o.sPrivField
                            ,sDescrizione: o.sPrivName
                        }]
                    });
                }
                
                return ao;
                
            }, []);
            
            if ( !forRoles ) {
                setAoPrivilegi(  aoRispostaPrivilegi?.data );
            }
            
            setAoStruttura(  aoStruttura );
            setBCaricamento( false );
            
        } catch ( oErrore ) {
            NotificationManager.error( oErrore?.response?.data?.message || configMessage.UNEXPECTER_ERROR, "", config.TIMEOUT_MSG_ERROR_GEN );
            
        }
    };
    
    /**
     * Gestisce il cambio di stato di un privilegio
     * @param {string} sPrivilegio - Chiave del privilegio
     * @param {number} nIndice     - Indice della colonna
     * Esempio: fCambiaPrivilegio('P_ALERTS', 0)
     */
    const fCambiaPrivilegio = ( sPrivilegio, nIndice ) => {
        
        const aoPrivilegiCopia = [ ...aoPrivilegi ];
        const sValoreAttuale   = aoPrivilegiCopia[ nIndice ][ sPrivilegio ];
        
        // Mappa delle transizioni di stato per i privilegi
        const oTransizioni = {
             0: { W: 'N', R: 'W', N: 'R' }  // Ciclo dei privilegi diretti:     N -> R -> W -> N
            ,1: { W: 'N', R: 'W', N: 'R' }  // Ciclo dei privilegi dai ruoli:   N -> R -> W -> N
            ,2: { Y: 'N', N: 'Y' }          // Toggle dei privilegi negati:     Y <-> N
        };
        
        if ( oTransizioni[ nIndice ]?.[ sValoreAttuale ] ) {
            aoPrivilegiCopia[ nIndice ][ sPrivilegio ] = oTransizioni[ nIndice ][ sValoreAttuale ];
            setAoPrivilegi( aoPrivilegiCopia );
            setBModificato( true );
        }
        
    };
    
    // Salva le modifiche ai privilegi
    const fSalvaModifiche = async () => {
        
        try {
            // Conversione dei parametri per le API
            const oParamPrivilegi = { userId: kUser, ...aoPrivilegi[ 0 ] };
            const oParamNegazioni = { userId: kUser, ...aoPrivilegi[ 2 ] };
            
            const [ oRetValRispostaModPrivs, oRetValRispostaModDeny ] = await Promise.all( [
                 fInviaRichiesta( '/users/user-mod-privs' , oParamPrivilegi , 'POST' )
                ,fInviaRichiesta( '/users/user-mod-deny'  , oParamNegazioni , 'POST' )
            ] );
            
            const oErrors = {
                 '-2': `Insufficient privileges to perform the operation`
                ,'-3': `User does not exist, or is an Administrator.`
                ,'-4': `No User Privileges specified.`
            };

            if ( oRetValRispostaModPrivs?.data < 1 ) {
                const sErrMsg = oErrors[ oRetValRispostaModPrivs?.data + '' ] || config.TIMEOUT_MSG_ERROR_GEN
                throw new Error(sErrMsg);
            }
            
            if ( oRetValRispostaModDeny?.data < 1 ) {
                const sErrMsg = oErrors[ oRetValRispostaModPrivs?.data + '' ] || config.TIMEOUT_MSG_ERROR_GEN
                throw new Error(sErrMsg);
            }
            
            // non serve, dato che è meglio evitare doppia notifica di successo
            // NotificationManager.success(configMessage.USER_SUCCESS, "", config.TIMEOUT_MSG_SUCCESS);
            
        } catch ( oErrore ) {
            NotificationManager.error(
                 'Error while updating privileges. ' +  ( oErrore?.response?.data?.message || configMessage.UNEXPECTER_ERROR )
                ,''
                ,config.TIMEOUT_MSG_ERROR_GEN
            );
            
        }
        
        await fCaricaDati();
        setSaveFalse();
        
    };
    
    /**
     * Invia una richiesta HTTP al backend
     * @param {string} sUrl    - URL dell'endpoint
     * @param {Object} oParams - Parametri della richiesta
     * @param {string} sMethod - get o post
     * @returns {Promise}        Promessa della risposta
     */
    const fInviaRichiesta = async ( sUrl, oParams, sMethod = 'GET' ) => {
        
        const oConfig = {
             url     : sUrl
            ,method  : sMethod
            ,baseURL : config.API_URL
            ,headers : {Authorization: `Bearer ${await auth.getAccessTokenForBE()}`}           
            ,params  : oParams
        };
        
        if ( sMethod.toUpperCase() === 'POST' ) {
            oConfig.handleError = true;
        }
        
        return axios(oConfig);
        
    };
    
    /**
     * Renderizza l'icona del privilegio
     * @param {number}   nIndice     - Indice della colonna (che indica anche il tipo di colonna)
     * @param {string}   sValore     - Valore del privilegio
     * @param {boolean}  bCliccabile - Se l'icona è cliccabile
     * @param {Function} fOnClick    - Funzione di callback per il click
     * @returns {JSX.Element}
     */
    const fRenderizzaIcona = ( nIndice, sValore, bCliccabile = false, fOnClick = null ) => {
                            // negazioni
        const oConfig = ( ( nIndice === 2 ) ? oIconeNegazione[ sValore ] : oIconePrivilegi[ sValore ] ) || {};
        const Icona   = oConfig?.oComponente || null;
        
        return (
            <div
                key       ={ nIndice }
                className ={ `privileges-ico ${ bCliccabile ? 'ico-link' : 'ico-no-link' }` }
                onClick   ={ bCliccabile ? fOnClick : null }
            >
                <div className ={ oConfig?.sClasse || '' } title={ oConfig?.sTitle || '' }>
                    { Icona && <Icona /> }
                </div>
            </div>
        );
        
    };
    
    if ( bCaricamento ) return <div id="privileges-list" />;
    
    return (
        <>
            { forRoles ? null :
                <div className="HeadTitle">
                    <div className="privilege-list-main-title">Privilege List</div>
                    <div className="privileges-list PrivilegeSticky">
                        <div className="blue-title">Result</div>
                        <div className="blue-title">Deny</div>
                        <div className="blue-title">Roles</div>
                        <div className="blue-title">Explicit</div>
                    </div>
                </div>
            }
            
            <div className="privileges-block">
                {/* ● CICLO 1 sulle SEZIONI in cui sono raggruppati i privilegi */}
                { aoStruttura.map( ( { sSection, aoPrivilegiDiSezione } ) => (
                    
                    <React.Fragment key={ sSection }>
                        
                        {/* Intestazione della sezione */}
                        <div className="privileges-list title">{ sSection }</div>
                        
                        {/* ● CICLO 2 sui PRIVILEGI della sezione */}
                        { aoPrivilegiDiSezione.map( ( { sPrivilegio, sDescrizione } ) => (
                            
                            <div className="privileges-list" key={ sPrivilegio }>
                                {/* Nome/descrizione del privilegio */}
                                <div className="privileges-title">{ sDescrizione }</div>
                                
                                {/* 
                                    ● CICLO 3 sugli STATI del privilegio [3,2,1,0]:
                                    
                                    3: risultato finale (W/R/N)
                                    2: privilegi negati (Y/N)
                                    1: privilegi ereditati dai ruoli (W/R/N)
                                    0: privilegi diretti (W/R/N)
                                */}
                                { ( forRoles ? [ 1 ] : [ 3, 2, 1, 0 ] ).map( nIndice => (
                                    fRenderizzaIcona(
                                        nIndice
                                        // Valore corrente del privilegio (W/R/N) o (Y/N)
                                        ,aoPrivilegi[ nIndice ][ sPrivilegio ]
                                        // Cliccabile solo per privilegi diretti (0) e negazioni (2)
                                        ,( forRoles ? [ 1 ] : [ 0, 2 ] ).includes( nIndice )
                                        // Callback per la modifica del privilegio
                                        ,() => fCambiaPrivilegio( sPrivilegio, nIndice )
                                    )
                                ))}
                                
                            </div>
                            
                        ))}
                        
                    </React.Fragment>
                    
                ))}
            </div>
        </>
    );
};

export default PrivilegeList;
